import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { RootState, store } from './configureStore';

export interface ReportState {
	error: boolean;
	isLoading: boolean;
	itemCountPerPage: number;
	lastPhotoNumber: number;
	isPublishing: boolean;
	isSaving: boolean;
	isUploadingExcel: boolean;
	confirmReportPublish: boolean;
	confirmExcelIsoUpload: boolean;
	excelDocumentUrl: string;
	base64: string;
}

const initialReportState: ReportState = {
	base64: '',
	error: false,
	isLoading: false,
	lastPhotoNumber: 0,
	itemCountPerPage: 2,
	confirmReportPublish: false,
	confirmExcelIsoUpload: false,
	isPublishing: false,
	isUploadingExcel: false,
	isSaving: false,
	excelDocumentUrl: '',
};

const slice = createSlice({
	name: 'reportDetails',
	initialState: initialReportState,
	reducers: {
		setBase64: (state, action: PayloadAction<string>) => {
			state.base64 = action.payload;
		},
		setExcelDocumentUrl: (state, action) => {
			state.excelDocumentUrl = action.payload;
		},
		setUploadingExcel: (state, action) => {
			state.isUploadingExcel = action.payload;
		},
		setConfirmExcelIsoUpload: (state, action) => {
			state.confirmExcelIsoUpload = action.payload;
		},
		setItemsPerPage: (state, action) => {
			state.itemCountPerPage = action.payload;
		},
		setPublishing: (state, action) => {
			state.isPublishing = action.payload;
		},
		setSaving: (state, action) => {
			state.isSaving = action.payload;
		},
		setError: (state, action) => {
			state.error = action.payload;
		},
		setConfirmReportPublish: (state, action) => {
			state.confirmReportPublish = action.payload;
		},
		setLastPhotoNumber: (state, action: PayloadAction<number>) => {
			state.lastPhotoNumber = action.payload;
		},
		cacheRequested: (state) => {
			state.isSaving = true;
			state.error = false;
		},
		cacheRequestSuccess: (state) => {
			state.isSaving = false;
			state.error = false;
		},
		cacheRequestFailed: (state) => {
			state.isSaving = false;
			state.error = true;
		}
	},
});

export const {
	cacheRequested,
	cacheRequestSuccess,
	cacheRequestFailed,
	setBase64,
	setConfirmExcelIsoUpload,
	setConfirmReportPublish,
	setLastPhotoNumber,
	setItemsPerPage,
	setPublishing,
	setExcelDocumentUrl,
	setUploadingExcel,
	setSaving,
	setError,
} = slice.actions;

export const updateLastPhotoNumber = () => {
	const nextPhotoNumber = store.getState().reportDetails.lastPhotoNumber + 1;
	store.dispatch(setLastPhotoNumber(nextPhotoNumber));

	return nextPhotoNumber;
};

export const setPhotoNumber = (photoNumber: number) => {
	store.dispatch(setLastPhotoNumber(photoNumber));
};

export const getReportDetails = createSelector(
	(state: RootState) => state,
	(state) => state.reportDetails
);

export default slice.reducer;
